import React, { useEffect, useMemo, useState } from "react";
import api, { extractError } from "@/lib/api";
import { PageHeader, LoadingState, EmptyState } from "@/components/Common";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { fmtDateTime } from "@/lib/format";
import { Settings, UserPlus, Pencil, Loader2, ShieldCheck, Search, Phone, Briefcase, MessageCircle, Send, CheckCircle2, XCircle, BellRing, LogIn, Landmark, KeyRound, Copy, Download } from "lucide-react";
import { VoiceSearchButton, appendSearch } from "@/components/VoiceSearchButton";
import { toast } from "sonner";

const emptyUser = { nome_completo: "", email: "", password: "", ruolo: "operatore", telefono: "", servizi_ids: [], enti_ids: [], attivo: true };

// Ruoli per i quali l'assegnazione dei servizi è rilevante (scoping pratiche)
const SERVICE_SCOPED_ROLES = ["responsabile", "operatore", "centralinista", "volontario"];

const Admin = () => {
  const [tab, setTab] = useState("utenti");
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [services, setServices] = useState([]);
  const [entities, setEntities] = useState([]);
  const [settings, setSettings] = useState({});
  const [audit, setAudit] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [edit, setEdit] = useState(null);
  const [uf, setUf] = useState(emptyUser);
  const [saving, setSaving] = useState(false);
  const [query, setQuery] = useState("");
  const [wa, setWa] = useState(null);
  const [waPhone, setWaPhone] = useState("");
  const [waBusy, setWaBusy] = useState(false);
  // Codici di riserva (generazione admin)
  const [bkCodes, setBkCodes] = useState([]);
  const [bkUser, setBkUser] = useState(null);
  const [bkOpen, setBkOpen] = useState(false);
  const [bkBusy, setBkBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [u, r, s, sv, en, w] = await Promise.all([
        api.get("/users"), api.get("/roles"), api.get("/settings"), api.get("/services"),
        api.get("/entities").catch(() => ({ data: [] })),
        api.get("/notifications/whatsapp/status").catch(() => ({ data: null })),
      ]);
      setUsers(u.data); setRoles(r.data); setSettings(s.data); setServices(sv.data); setEntities(en.data); setWa(w.data);
    } catch (e) { toast.error(extractError(e)); } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);
  useEffect(() => { if (tab === "audit") api.get("/audit?limit=150").then(({ data }) => setAudit(data)).catch(() => {}); }, [tab]);

  const svcName = useMemo(() => Object.fromEntries(services.map((s) => [s.id, s.nome])), [services]);
  const enteName = useMemo(() => Object.fromEntries(entities.map((e) => [e.id, e.denominazione])), [entities]);

  const filteredUsers = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return users;
    return users.filter((u) =>
      (u.nome_completo || "").toLowerCase().includes(q) ||
      (u.email || "").toLowerCase().includes(q) ||
      (u.ruolo_label || "").toLowerCase().includes(q));
  }, [users, query]);

  const openUser = (u) => {
    setEdit(u);
    setUf(u ? { ...emptyUser, ...u, password: "", servizi_ids: u.servizi_ids || [], enti_ids: u.enti_ids || [] } : emptyUser);
    setOpen(true);
  };

  const toggleService = (id) => setUf((s) => ({
    ...s,
    servizi_ids: s.servizi_ids.includes(id) ? s.servizi_ids.filter((x) => x !== id) : [...s.servizi_ids, id],
  }));

  const toggleEnte = (id) => setUf((s) => ({
    ...s,
    enti_ids: (s.enti_ids || []).includes(id) ? s.enti_ids.filter((x) => x !== id) : [...(s.enti_ids || []), id],
  }));

  const saveUser = async () => {
    if (!uf.nome_completo || !uf.email) { toast.error("Nome ed email obbligatori"); return; }
    if (!edit && !uf.password) { toast.error("Imposta una password"); return; }
    if (!edit && uf.password.length < 6) { toast.error("La password deve avere almeno 6 caratteri"); return; }
    setSaving(true);
    try {
      const payload = {
        nome_completo: uf.nome_completo, ruolo: uf.ruolo, telefono: uf.telefono || null,
        servizi_ids: uf.servizi_ids || [], enti_ids: uf.enti_ids || [], attivo: uf.attivo,
      };
      if (edit) {
        if (uf.password) payload.password = uf.password;
        await api.put(`/users/${edit.id}`, payload);
      } else {
        await api.post("/users", { ...payload, email: uf.email, password: uf.password });
      }
      toast.success("Utente salvato"); setOpen(false); load();
    } catch (e) { toast.error(extractError(e)); } finally { setSaving(false); }
  };

  const deactivate = async (u) => {
    if (!window.confirm(`Disattivare ${u.nome_completo}?`)) return;
    try { await api.delete(`/users/${u.id}`); toast.success("Utente disattivato"); load(); }
    catch (e) { toast.error(extractError(e)); }
  };

  const genBackupForUser = async (u) => {
    if (!window.confirm(`Generare nuovi codici di riserva per ${u.nome_completo}? Eventuali codici precedenti non saranno più validi.`)) return;
    setBkBusy(true);
    try {
      const { data } = await api.post(`/users/${u.id}/backup-codes/generate`);
      setBkCodes(data.codes || []);
      setBkUser(u);
      setOpen(false);
      setBkOpen(true);
      load();
    } catch (e) { toast.error(extractError(e)); } finally { setBkBusy(false); }
  };

  const copyAdminCodes = async () => {
    try { await navigator.clipboard.writeText(bkCodes.join("\n")); toast.success("Codici copiati negli appunti."); }
    catch { toast.error("Copia non riuscita. Copia i codici manualmente."); }
  };

  const downloadAdminCodes = () => {
    const header = "INSIEME RAS - Codici di riserva (accesso)\nUtente: " + (bkUser?.email || "") +
      "\nGenerati il: " + new Date().toLocaleString("it-IT") +
      "\n\nOgni codice è utilizzabile UNA SOLA VOLTA. Consegnare all'utente in modo sicuro.\n\n";
    const blob = new Blob([header + bkCodes.join("\n") + "\n"], { type: "text/plain;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = "codici-riserva-" + ((bkUser?.email || "utente").split("@")[0]) + ".txt";
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
  };

  const saveSetting = async (key, value) => {
    try { await api.put("/settings", { key, value }); setSettings((s) => ({ ...s, [key]: value })); toast.success("Impostazione aggiornata"); }
    catch (e) { toast.error(extractError(e)); }
  };

  const toggleWhatsapp = async (value) => {
    try {
      await api.put("/settings", { key: "whatsapp_enabled", value });
      setSettings((s) => ({ ...s, whatsapp_enabled: value }));
      setWa((w) => (w ? { ...w, enabled: value } : w));
      toast.success(value ? "Notifiche WhatsApp abilitate" : "Notifiche WhatsApp disabilitate");
    } catch (e) { toast.error(extractError(e)); }
  };

  const sendWaTest = async () => {
    if (!waPhone.trim()) { toast.error("Inserisci un numero (formato +39 ...)"); return; }
    setWaBusy(true);
    try {
      const { data } = await api.post("/notifications/whatsapp/test", { to: waPhone.trim() });
      if (data.sent) toast.success("Messaggio di prova inviato");
      else if (data.status === "non_configurato") toast.error("WhatsApp non configurato: inserisci le credenziali Meta in backend/.env");
      else toast.error(`Invio non riuscito: ${data.error || data.status}`);
    } catch (e) { toast.error(extractError(e)); } finally { setWaBusy(false); }
  };

  const runReminders = async () => {
    setWaBusy(true);
    try {
      const { data } = await api.post("/notifications/whatsapp/run-reminders");
      toast.success(`Promemoria elaborati: ${data.considered} · inviati: ${data.sent} · saltati: ${data.skipped}`);
    } catch (e) { toast.error(extractError(e)); } finally { setWaBusy(false); }
  };

  const showServiceAssign = SERVICE_SCOPED_ROLES.includes(uf.ruolo);

  return (
    <div>
      <PageHeader icon={Settings} title="Amministrazione" description="Utenti, ruoli, impostazioni e registro attività" />
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="flex-wrap h-auto">
          <TabsTrigger value="utenti" data-testid="admin-tab-utenti">Utenti</TabsTrigger>
          <TabsTrigger value="impostazioni" data-testid="admin-tab-impostazioni">Impostazioni</TabsTrigger>
          <TabsTrigger value="ruoli" data-testid="admin-tab-ruoli">Ruoli e permessi</TabsTrigger>
          <TabsTrigger value="audit" data-testid="admin-tab-audit">Registro attività</TabsTrigger>
        </TabsList>

        <TabsContent value="utenti" className="mt-4">
          <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-3">
            <div className="relative flex-1 max-w-sm">
              <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Cerca per nome, email o ruolo…" className="pl-9 pr-11" data-testid="user-search-input" aria-label="Cerca utenti" />
              <div className="absolute right-2 top-1/2 -translate-y-1/2"><VoiceSearchButton onResult={(t) => setQuery((p) => appendSearch(p, t))} data-testid="user-search-mic" /></div>
            </div>
            <div className="sm:ml-auto">
              <Button onClick={() => openUser(null)} className="bg-[color:var(--brand-orange-600)] hover:bg-[color:var(--brand-orange-500)] text-white" data-testid="new-user-button">
                <UserPlus className="h-4 w-4 mr-2" /> Nuovo utente
              </Button>
            </div>
          </div>
          {loading ? <LoadingState /> : filteredUsers.length === 0 ? (
            <EmptyState title="Nessun utente trovato" description="Modifica la ricerca o crea un nuovo utente." />
          ) : (
            <Card className="divide-y" data-testid="users-list">
              {filteredUsers.map((u) => (
                <div key={u.id} className="p-4 flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <div className="font-medium truncate">{u.nome_completo}</div>
                    <div className="text-xs text-muted-foreground truncate">{u.email} · {u.ruolo_label}</div>
                    <div className="flex flex-wrap items-center gap-2 mt-1.5">
                      {u.telefono && <span className="inline-flex items-center gap-1 text-[11px] text-muted-foreground"><Phone className="h-3 w-3" /> {u.telefono}</span>}
                      {(u.servizi_ids || []).length > 0 && (
                        <span className="inline-flex items-center gap-1 text-[11px] text-[color:var(--brand-blue-700)]" title={(u.servizi_ids || []).map((id) => svcName[id]).filter(Boolean).join(", ")}>
                          <Briefcase className="h-3 w-3" /> {(u.servizi_ids || []).length} servizi
                        </span>
                      )}
                      {(u.enti_ids || []).length > 0 && (
                        <span className="inline-flex items-center gap-1 text-[11px] text-[color:var(--brand-green-700)]" title={(u.enti_ids || []).map((id) => enteName[id]).filter(Boolean).join(", ")}>
                          <Landmark className="h-3 w-3" /> {(u.enti_ids || []).length} enti
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <span className={`text-[10px] rounded-full px-2 py-0.5 ${u.attivo ? "bg-[color:var(--brand-green-100)] text-[color:var(--brand-green-700)]" : "bg-slate-100 text-slate-500"}`}>{u.attivo ? "Attivo" : "Disattivato"}</span>
                    <Button variant="ghost" size="sm" onClick={() => openUser(u)} data-testid={`edit-user-${u.id}`} aria-label={`Modifica ${u.nome_completo}`}><Pencil className="h-4 w-4" /></Button>
                  </div>
                </div>
              ))}
            </Card>
          )}
        </TabsContent>

        <TabsContent value="impostazioni" className="mt-4">
          <div className="grid md:grid-cols-2 gap-4">
            <Card className="p-4">
              <h3 className="font-semibold mb-2">Assistente AI</h3>
              <div className="flex items-center justify-between"><Label htmlFor="ai-switch">Funzioni AI attive</Label><Switch id="ai-switch" checked={!!settings.ai_enabled} onCheckedChange={(v) => saveSetting("ai_enabled", v)} /></div>
              <p className="text-xs text-muted-foreground mt-2">Disattivando, le funzioni AI risulteranno non disponibili per tutti gli utenti.</p>
            </Card>
            <Card className="p-4">
              <h3 className="font-semibold mb-2">Notifiche email</h3>
              <Label htmlFor="email_override">Indirizzo di override (modalità demo)</Label>
              <div className="flex gap-2 mt-1">
                <Input defaultValue={settings.email_override || ""} id="email_override" placeholder="vuoto = invia al responsabile del servizio" />
                <Button onClick={() => saveSetting("email_override", document.getElementById("email_override").value)}>Salva</Button>
              </div>
              <p className="text-xs text-muted-foreground mt-2">Se valorizzato, tutte le email di notifica vengono inviate a questo indirizzo (utile per test). Lascia vuoto per inviare ai responsabili reali dei servizi.</p>
            </Card>
          </div>

          <Card className="p-4 mt-4" data-testid="whatsapp-settings-card">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div className="flex items-center gap-2">
                <MessageCircle className="h-5 w-5 text-[color:var(--brand-green-700)]" />
                <h3 className="font-semibold">Notifiche WhatsApp (Meta Cloud API)</h3>
              </div>
              {wa && (
                <span className={`inline-flex items-center gap-1.5 text-xs rounded-full px-2.5 py-1 ${wa.configured ? "bg-[color:var(--brand-green-100)] text-[color:var(--brand-green-700)]" : "bg-[color:var(--brand-orange-100)] text-[color:var(--brand-orange-600)]"}`} data-testid="whatsapp-status-badge">
                  {wa.configured ? <CheckCircle2 className="h-3.5 w-3.5" /> : <XCircle className="h-3.5 w-3.5" />}
                  {wa.configured ? "Configurato" : "Non configurato"}
                </span>
              )}
            </div>

            {!wa?.configured && (
              <p className="text-sm text-muted-foreground mt-3">
                L'integrazione è pronta ma non attiva: inserisci le credenziali Meta in <code className="text-xs bg-muted px-1 py-0.5 rounded">backend/.env</code> e crea i template su WhatsApp Manager.
                Le istruzioni complete e i testi dei template sono in <code className="text-xs bg-muted px-1 py-0.5 rounded">/app/memory/whatsapp_setup.md</code>.
                Finché non è configurato, non viene inviato alcun messaggio (l'email resta il canale garantito).
              </p>
            )}

            <div className="flex items-center justify-between mt-4">
              <Label htmlFor="wa-switch">Notifiche WhatsApp attive</Label>
              <Switch id="wa-switch" checked={wa ? !!wa.enabled : !!settings.whatsapp_enabled} onCheckedChange={toggleWhatsapp} data-testid="whatsapp-enabled-switch" />
            </div>

            {wa && (
              <div className="grid sm:grid-cols-2 gap-x-6 gap-y-1.5 mt-4 text-xs">
                {[
                  ["Phone Number ID", wa.fields?.phone_number_id],
                  ["Access Token", wa.fields?.access_token],
                  ["WABA ID", wa.fields?.waba_id],
                  ["App Secret", wa.fields?.app_secret],
                  ["Webhook Verify Token", wa.fields?.webhook_verify_token],
                ].map(([label, ok]) => (
                  <div key={label} className="flex items-center justify-between border-b border-dashed py-1">
                    <span className="text-muted-foreground">{label}</span>
                    <span className={`inline-flex items-center gap-1 ${ok ? "text-[color:var(--brand-green-700)]" : "text-muted-foreground"}`}>
                      {ok ? <CheckCircle2 className="h-3.5 w-3.5" /> : <XCircle className="h-3.5 w-3.5" />}{ok ? "impostato" : "mancante"}
                    </span>
                  </div>
                ))}
              </div>
            )}

            <div className="grid sm:grid-cols-2 gap-4 mt-5">
              <div>
                <Label htmlFor="wa-test-phone">Invia messaggio di prova</Label>
                <div className="flex gap-2 mt-1">
                  <Input id="wa-test-phone" type="tel" value={waPhone} onChange={(e) => setWaPhone(e.target.value)} placeholder="+39 ..." data-testid="whatsapp-test-phone" />
                  <Button onClick={sendWaTest} disabled={waBusy} className="shrink-0 bg-[color:var(--brand-green-700)] hover:opacity-90 text-white" data-testid="whatsapp-test-button">
                    {waBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                  </Button>
                </div>
                <p className="text-[11px] text-muted-foreground mt-1">Usa il template "{wa?.templates?.nuova_richiesta || "ras_nuova_richiesta"}" (deve essere approvato).</p>
              </div>
              <div>
                <Label>Promemoria attività in scadenza</Label>
                <div className="mt-1">
                  <Button variant="outline" onClick={runReminders} disabled={waBusy} className="w-full" data-testid="whatsapp-reminders-button">
                    <BellRing className="h-4 w-4 mr-2" /> Invia promemoria ora
                  </Button>
                </div>
                <p className="text-[11px] text-muted-foreground mt-1">Invia un promemoria WhatsApp agli assegnatari delle attività in scadenza oggi o scadute.</p>
              </div>
            </div>

            {wa?.templates && (
              <div className="mt-5 rounded-lg border bg-[color:var(--brand-blue-100)]/30 p-3">
                <p className="text-xs font-medium text-slate-700 mb-1.5">Template attesi (categoria utility, lingua {wa.language}):</p>
                <ul className="text-[11px] text-muted-foreground space-y-0.5 font-mono">
                  <li>• {wa.templates.nuova_richiesta} — nuova richiesta dal portale</li>
                  <li>• {wa.templates.pratica_assegnata} — assegnazione pratica</li>
                  <li>• {wa.templates.promemoria_attivita} — promemoria attività</li>
                </ul>
              </div>
            )}
          </Card>
        </TabsContent>

        <TabsContent value="ruoli" className="mt-4">
          <div className="grid md:grid-cols-2 gap-4">{roles.map((r) => (
            <Card key={r.key} className="p-4"><div className="flex items-center gap-2 mb-2"><ShieldCheck className="h-4 w-4 text-[color:var(--brand-blue-600)]" /><h3 className="font-semibold">{r.label}</h3></div>
              <div className="flex flex-wrap gap-1">{r.permessi.map((p) => <span key={p} className="text-[10px] rounded-full bg-slate-100 px-2 py-0.5 font-mono">{p}</span>)}</div>
            </Card>
          ))}</div>
        </TabsContent>

        <TabsContent value="audit" className="mt-4 space-y-5">
          {/* Elenco utenti con ultimo accesso */}
          <div>
            <div className="flex items-center gap-2 mb-2">
              <LogIn className="h-4 w-4 text-[color:var(--brand-blue-600)]" />
              <h3 className="font-semibold text-sm">Utenti e ultimo accesso</h3>
            </div>
            <Card className="divide-y" data-testid="admin-users-access">
              {[...users].sort((a, b) => (b.last_login || "").localeCompare(a.last_login || "")).map((u) => (
                <div key={u.id} className="p-3 text-sm flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <div className="font-medium truncate">{u.nome_completo}</div>
                    <div className="text-xs text-muted-foreground truncate">{u.email} · {u.ruolo_label || u.ruolo}</div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className="text-xs text-muted-foreground">Ultimo accesso</div>
                    <div className={`text-sm font-medium ${u.last_login ? "text-[color:var(--brand-green-700)]" : "text-muted-foreground"}`} data-testid={`user-last-login-${u.id}`}>
                      {u.last_login ? fmtDateTime(u.last_login) : "Mai effettuato"}
                    </div>
                  </div>
                </div>
              ))}
            </Card>
          </div>

          {/* Registro attività (audit log) */}
          <div>
            <h3 className="font-semibold text-sm mb-2">Registro attività</h3>
            {audit.length === 0 ? <EmptyState title="Nessuna voce nel registro" /> : (
              <Card className="divide-y max-h-[60vh] overflow-auto ras-scroll">{audit.map((a) => (
                <div key={a.id} className="p-3 text-sm flex items-center justify-between gap-2">
                  <div><span className="font-medium">{a.action}</span> <span className="text-muted-foreground">su {a.entity}</span> {a.outcome !== "success" && <span className="text-[color:var(--brand-red-600)]">({a.outcome})</span>}<div className="text-xs text-muted-foreground">{a.user_name || "Sistema"} · {a.origin}</div></div>
                  <span className="text-xs text-muted-foreground shrink-0">{fmtDateTime(a.timestamp)}</span>
                </div>
              ))}</Card>
            )}
          </div>
        </TabsContent>
      </Tabs>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto ras-scroll">
          <DialogHeader><DialogTitle className="font-display">{edit ? "Modifica utente" : "Nuovo utente"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label htmlFor="user-name">Nome completo</Label><Input id="user-name" value={uf.nome_completo} onChange={(e) => setUf({ ...uf, nome_completo: e.target.value })} data-testid="user-name-input" /></div>
            <div><Label htmlFor="user-email">Email</Label><Input id="user-email" type="email" value={uf.email} onChange={(e) => setUf({ ...uf, email: e.target.value })} disabled={!!edit} data-testid="user-email-input" /></div>
            <div><Label htmlFor="user-phone">Telefono</Label><Input id="user-phone" type="tel" value={uf.telefono || ""} onChange={(e) => setUf({ ...uf, telefono: e.target.value })} placeholder="+39 ..." data-testid="user-phone-input" /></div>
            <div><Label htmlFor="user-password">{edit ? "Nuova password (lascia vuoto per non cambiare)" : "Password"}</Label><Input id="user-password" type="password" value={uf.password} onChange={(e) => setUf({ ...uf, password: e.target.value })} placeholder={edit ? "••••••" : "Min. 6 caratteri"} data-testid="user-password-input" /></div>
            <div><Label htmlFor="user-role">Ruolo</Label><Select value={uf.ruolo} onValueChange={(v) => setUf({ ...uf, ruolo: v })}><SelectTrigger id="user-role" data-testid="user-role-select"><SelectValue /></SelectTrigger><SelectContent>{roles.map((r) => <SelectItem key={r.key} value={r.key}>{r.label}</SelectItem>)}</SelectContent></Select></div>

            {showServiceAssign && (
              <div>
                <div className="flex items-center justify-between mb-1">
                  <Label>Servizi assegnati</Label>
                  <div className="flex gap-2">
                    <button type="button" className="text-[11px] text-[color:var(--brand-blue-600)] hover:underline" onClick={() => setUf((s) => ({ ...s, servizi_ids: services.map((x) => x.id) }))} data-testid="services-select-all">Tutti</button>
                    <button type="button" className="text-[11px] text-muted-foreground hover:underline" onClick={() => setUf((s) => ({ ...s, servizi_ids: [] }))} data-testid="services-select-none">Nessuno</button>
                  </div>
                </div>
                <ScrollArea className="h-40 rounded-md border p-2">
                  <div className="space-y-1.5">
                    {services.length === 0 ? <p className="text-xs text-muted-foreground px-1">Nessun servizio disponibile.</p> :
                      services.map((s) => (
                        <label key={s.id} htmlFor={`svc-${s.id}`} className="flex items-start gap-2 rounded px-1.5 py-1 hover:bg-muted cursor-pointer">
                          <Checkbox id={`svc-${s.id}`} checked={uf.servizi_ids.includes(s.id)} onCheckedChange={() => toggleService(s.id)} className="mt-0.5" data-testid={`service-checkbox-${s.id}`} />
                          <span className="text-sm leading-snug">{s.nome}</span>
                        </label>
                      ))}
                  </div>
                </ScrollArea>
                <p className="text-[11px] text-muted-foreground mt-1">Le pratiche dei servizi assegnati saranno visibili a questo utente (minimo privilegio).</p>
              </div>
            )}

            <div>
              <div className="flex items-center justify-between mb-1">
                <Label className="flex items-center gap-1.5"><Landmark className="h-3.5 w-3.5" /> Enti associati</Label>
                <div className="flex gap-2">
                  <button type="button" className="text-[11px] text-[color:var(--brand-blue-600)] hover:underline" onClick={() => setUf((s) => ({ ...s, enti_ids: entities.map((x) => x.id) }))} data-testid="enti-select-all">Tutti</button>
                  <button type="button" className="text-[11px] text-muted-foreground hover:underline" onClick={() => setUf((s) => ({ ...s, enti_ids: [] }))} data-testid="enti-select-none">Nessuno</button>
                </div>
              </div>
              <ScrollArea className="h-36 rounded-md border p-2">
                <div className="space-y-1.5">
                  {entities.length === 0 ? <p className="text-xs text-muted-foreground px-1">Nessun ente disponibile.</p> :
                    entities.map((e) => (
                      <label key={e.id} htmlFor={`ente-${e.id}`} className="flex items-start gap-2 rounded px-1.5 py-1 hover:bg-muted cursor-pointer">
                        <Checkbox id={`ente-${e.id}`} checked={(uf.enti_ids || []).includes(e.id)} onCheckedChange={() => toggleEnte(e.id)} className="mt-0.5" data-testid={`ente-checkbox-${e.id}`} />
                        <span className="text-sm leading-snug">{e.denominazione}</span>
                      </label>
                    ))}
                </div>
              </ScrollArea>
              <p className="text-[11px] text-muted-foreground mt-1">Gli enti associati compaiono nel banner della Dashboard dell'utente.</p>
            </div>

            <div className="flex items-center gap-2"><Switch id="user-active" checked={uf.attivo} onCheckedChange={(v) => setUf({ ...uf, attivo: v })} data-testid="user-active-switch" /><Label htmlFor="user-active">Attivo</Label></div>

            {edit && (
              <div className="rounded-lg border p-3" data-testid="admin-backup-section">
                <div className="flex items-center gap-2 mb-1">
                  <ShieldCheck className="h-4 w-4 text-[color:var(--brand-orange-600)]" />
                  <span className="text-sm font-medium">Codici di riserva (2FA)</span>
                  {typeof edit.backup_codes_remaining === "number" && (
                    <Badge variant="secondary" className="ml-auto" data-testid="admin-backup-count">{edit.backup_codes_remaining} attivi</Badge>
                  )}
                </div>
                <p className="text-[11px] text-muted-foreground mb-2">Genera codici di emergenza se l'utente non riesce a ricevere l'email con il codice di accesso. Da consegnare in modo sicuro.</p>
                <Button variant="outline" className="w-full" onClick={() => genBackupForUser(edit)} disabled={bkBusy} data-testid="admin-gen-backup-button">
                  {bkBusy ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <KeyRound className="h-4 w-4 mr-2" />} Genera codici di riserva
                </Button>
              </div>
            )}

            {edit && <Button variant="outline" className="w-full text-[color:var(--brand-red-600)]" onClick={() => { setOpen(false); deactivate(edit); }} data-testid="user-deactivate-button">Disattiva utente</Button>}
          </div>
          <DialogFooter className="gap-2"><Button variant="outline" onClick={() => setOpen(false)}>Annulla</Button><Button onClick={saveUser} disabled={saving} className="bg-[color:var(--brand-orange-600)] hover:bg-[color:var(--brand-orange-500)] text-white" data-testid="user-save-button">{saving && <Loader2 className="h-4 w-4 animate-spin mr-2" />}Salva</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog: codici di riserva generati dall'admin (mostrati una sola volta) */}
      <Dialog open={bkOpen} onOpenChange={setBkOpen}>
        <DialogContent data-testid="admin-backup-dialog">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2">
              <KeyRound className="h-5 w-5 text-[color:var(--brand-orange-600)]" /> Codici di riserva
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            Codici per <span className="font-medium text-foreground">{bkUser?.nome_completo}</span> ({bkUser?.email}).
            Consegnali all'utente in modo sicuro: non potrai più rivederli e ciascuno funziona una sola volta.
          </p>
          <div className="flex items-start gap-2 rounded-lg border bg-[color:var(--brand-orange-100)]/50 p-3 text-xs text-slate-600">
            <ShieldCheck className="h-4 w-4 text-[color:var(--brand-orange-600)] shrink-0 mt-0.5" />
            <span>Gli eventuali codici precedenti dell'utente non sono più validi.</span>
          </div>
          <div className="grid grid-cols-2 gap-2 my-1" data-testid="admin-backup-list">
            {bkCodes.map((c, i) => (
              <div key={c} className="rounded-md border bg-muted/40 px-3 py-2 font-mono text-sm tracking-widest text-center" data-testid={`admin-backup-code-${i}`}>{c}</div>
            ))}
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={copyAdminCodes} data-testid="admin-backup-copy-button"><Copy className="h-4 w-4 mr-2" /> Copia</Button>
            <Button variant="outline" onClick={downloadAdminCodes} data-testid="admin-backup-download-button"><Download className="h-4 w-4 mr-2" /> Scarica .txt</Button>
            <Button onClick={() => setBkOpen(false)} className="bg-[color:var(--brand-blue-600)] hover:bg-[color:var(--brand-blue-700)] text-white" data-testid="admin-backup-done-button"><CheckCircle2 className="h-4 w-4 mr-2" /> Fatto</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Admin;
